// Derives a first-run "Get started" checklist from live app state, so each step
// auto-checks as the operator actually does it. Pure + deterministic for tests.
//
// Completion signals are intentionally conservative:
// - provider: a NON-local BYOK provider is configured (ollama_local is always
//   "configured" by default, so it must not count on its own).
// - brand: an industry pack was applied, or the brand name was changed off the
//   shipped mock default.
// - create/review/publish: derived from real runs/assets state.
import { mockBrandProfile } from "../data/mockBrand";

const APPROVED_STATUSES = ["approved", "scheduled", "published"];

export function buildOnboardingChecklist(state = {}) {
  const providers = state.modelSettings?.providers || [];
  const hasModel = providers.some((p) => p.configured && !p.local);

  const brand = state.brand || {};
  const brandSet =
    Boolean(brand.industry) ||
    (Boolean(brand.brand_name) && brand.brand_name !== mockBrandProfile.brand_name);

  const runs = state.runs || [];
  const createdPost = runs.length > 0;

  const assets = state.assets || [];
  const reviewed = assets.some((a) => APPROVED_STATUSES.includes(a.status));
  const published = assets.some((a) => a.status === "published");

  const steps = [
    {
      id: "provider",
      label: "Connect an AI model",
      description: "Add a provider API key in Settings so the agent can write.",
      to: "/settings",
      cta: "Open Settings",
      done: hasModel,
    },
    {
      id: "brand",
      label: "Set your Brand Profile",
      description: "Pick an industry starter pack so every post sounds like you.",
      to: "/brands",
      cta: "Set brand",
      done: brandSet,
    },
    {
      id: "create",
      label: "Create your first post",
      description: "Describe a post and let the agent draft platform-ready copy.",
      to: "/create",
      cta: "Create a post",
      done: createdPost,
    },
    {
      id: "review",
      label: "Review & approve a draft",
      description: "Open the preview, then approve or schedule it.",
      to: "/library",
      cta: "Open Library",
      done: reviewed,
    },
    {
      id: "publish",
      label: "Publish your first post",
      description: "Connect a channel, then publish or schedule a draft.",
      to: "/integrations",
      cta: "Connect a channel",
      done: published,
      optional: true,
    },
  ];

  const core = steps.filter((s) => !s.optional);
  const completedCore = core.filter((s) => s.done).length;
  const nextStep = steps.find((s) => !s.done) || null;

  return {
    steps,
    completedCore,
    totalCore: core.length,
    allCoreDone: completedCore === core.length,
    nextStep,
  };
}
